import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { Box, Chip, IconButton, MenuItem, Select, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Label from "../../../components/label";
import {
  setAdminCalendarEndDate,
  setAdminCalendarPageTab,
  setAdminCalendarStartDate,
  setSelectedAgentsForCalendar,
} from "../../../lib/redux/slices/searchKeysSlice";
import { getAllAgents } from "../../../services/AgentApi";
import { handleOpenSnackBar } from "../../../utils/helper";
import AppHeader from "./../../../components/header/index";
import ContractEndDateComponent from "./ContractEnd";
import FollowUpComponent from "./FollowUp";
import DynamicTable from "../../../components/dynamicTable";
import { getContractEndDateCount } from "../../../services/LeadProposalApi";
import CustomRangePicker from "../../../components/dateRange";
import moment from "moment";
import AppLoader from "../../../components/loader";
import PaginatedTable from "../../../components/paginatedTable";
import GlobalButton from "../../../components/button";
import FutureContract from "./FutureContract";
import MeetingDatesComponent from "./MeetingsDates";

// const tableHeader = ["Agent", "Contract End Date Count"];
const tableHeader = [
  {
    id: 1,
    align: "start",
    title: "Agent",
  },
  {
    id: 1,
    align: "center",
    title: "Contract End Date Count",
  },
];
const displayRows = ["name", "end_date_count"];

const dropDownStyle = {
  display: "flex",
  overflowY: "auto",
  gap: 0.5,

  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
    display: "none",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "primary.main",
    // borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "secondary.dark",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "text.coolGrey",
  },
};

const CalendarPageForAdmin = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allAgents, setAllAgents] = useState([]);

  const [contractEndDateCount, setContractEndDateCount] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);

  useEffect(() => {
    handleFetchAllGents();
    fetchContractEndDateCount(
      searchKeys?.adminCalendarStartDate,
      searchKeys?.adminCalendarEndDate
    );
  }, []);

  const handleFetchAllGents = async () => {
    try {
      const response = await getAllAgents();
      if (response?.data.status === "success") {
        let data = response.data.data;
        setAllAgents(data);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    }
  };

  const fetchContractEndDateCount = async (st, en) => {
    try {
      setIsLoading(true);
      const response = await getContractEndDateCount(st, en);
      if (response?.data.status === "success") {
        let data = response.data.data;
        setContractEndDateCount(data);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterByDate = () => {
    fetchContractEndDateCount(
      searchKeys?.adminCalendarStartDate,
      searchKeys?.adminCalendarEndDate
    );
  };

  const handleResetFilter = () => {
    fetchContractEndDateCount(moment(new Date()), moment(new Date()));
  };

  return (
    <AppHeader>
      <AppLoader isVisible={isLoading} />

      <Stack direction={"row"} alignItems={"center"} gap={1}>
        <GlobalButton
          variant={
            searchKeys.adminCalendarPageTab === 0
              ? "custom_gradient"
              : "contained"
          }
          title={"Sale Calendar"}
          style={{ borderRadius: 1 }}
          handleClickBtn={() => dispatch(setAdminCalendarPageTab(0))}
        />
        <GlobalButton
          variant={
            searchKeys.adminCalendarPageTab === 1
              ? "custom_gradient"
              : "contained"
          }
          title={"Provisioning Calendar"}
          style={{ borderRadius: 1 }}
          handleClickBtn={() => dispatch(setAdminCalendarPageTab(1))}
        />
      </Stack>

      {searchKeys.adminCalendarPageTab === 0 ? (
        <>
          <Stack
            direction={"row"}
            // justifyContent={"flex-end"}
            // alignItems={"flex-end"}
            spacing={1}
            mt={3}
            mb={3}
          >
            <IconButton
              onClick={handleResetFilter}
              sx={{
                borderRadius: "10px",
                background: (theme) => theme.palette.gradient.button,
                // marginBottom: "10px",
              }}
            >
              <RestartAltIcon htmlColor="white" />
            </IconButton>
            <CustomRangePicker
              startDate={searchKeys?.adminCalendarStartDate}
              endDate={searchKeys?.adminCalendarEndDate}
              setStartDate={(start) =>
                dispatch(setAdminCalendarStartDate(start))
              }
              setEndDate={(start) => dispatch(setAdminCalendarEndDate(start))}
              handleFilterDate={handleFilterByDate}
              stackProps={{
                borderRadius: "6px",
                border: "1px solid #80808057",
              }}
            />
          </Stack>
          <Box
            border={"1px solid black"}
            borderColor={"primary.main"}
            display={"inline-block"}
          >
            <PaginatedTable
              tableWidth={"600px"}
              minWidth={"600px"}
              tableHeader={tableHeader}
              displayRows={displayRows}
              tableData={contractEndDateCount || []}
              showPagination={false}
            />
          </Box>
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"flex-end"}
            mb={2}
            mt={2}
            spacing={1}
          >
            <Box width={{ xs: "100%", md: 250 }}>
              <Label title={"select agents"} style={{ fontSize: "0.75rem" }} />
              <Select
                fullWidth
                size="small"
                multiple
                value={searchKeys?.selectedAgentsForCalendar}
                onChange={(e) => {
                  dispatch(setSelectedAgentsForCalendar(e.target.value));
                }}
                renderValue={(selected) => (
                  <Box sx={{ ...dropDownStyle }}>
                    {selected.map((id, index) => {
                      const agent = allAgents.find((agent) => agent._id === id);
                      return <Chip key={id} label={agent?.username} />;
                    })}
                  </Box>
                )}
              >
                {allAgents.map((val, index) => (
                  <MenuItem key={index} value={val._id}>
                    {val.username}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <IconButton
              onClick={() => {
                dispatch(setSelectedAgentsForCalendar([]));
              }}
              sx={{
                borderRadius: "10px",
                background: (theme) => theme.palette.gradient.button,
              }}
            >
              <RestartAltIcon htmlColor="white" />
            </IconButton>
          </Stack>

          <Stack gap={4}>
            <FollowUpComponent />
            <ContractEndDateComponent />

            <MeetingDatesComponent />
          </Stack>
        </>
      ) : (
        <Box my={3}>
          <FutureContract />
        </Box>
      )}
    </AppHeader>
  );
};

export default CalendarPageForAdmin;
